import * as React from 'react';
import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { Icons } from './icons';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: 'horizontal' | 'vertical';
  setApi?: (api: CarouselApi) => void;
};

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
} & CarouselProps;

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

const useCarousel = () => {
  const context = React.useContext(CarouselContext);

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />');
  }

  return context;
};

const Carousel = ({ orientation = 'horizontal', opts, setApi, plugins, className, children, ...props }: React.ComponentProps<'div'> & CarouselProps) => {
  const autoplayPlugin = React.useRef(Autoplay({ delay: 3000, stopOnInteraction: true }));

  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      axis: orientation === 'horizontal' ? 'x' : 'y',
    },
    plugins ?? [autoplayPlugin.current]
  );
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);

  const onSelect = React.useCallback((api: CarouselApi) => {
    if (!api) return;
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);

  const scrollPrev = React.useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = React.useCallback(() => {
    api?.scrollNext();
  }, [api]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        scrollNext();
      }
    },
    [scrollPrev, scrollNext]
  );

  React.useEffect(() => {
    if (!api || !setApi) return;
    setApi(api);
  }, [api, setApi]);

  React.useEffect(() => {
    if (!api) return;
    onSelect(api);
    api.on('reInit', onSelect);
    api.on('select', onSelect);

    return () => {
      api?.off('select', onSelect);
    };
  }, [api, onSelect]);

  return (
    <CarouselContext.Provider
      value={{
        carouselRef,
        api: api,
        opts,
        orientation: orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
      }}
    >
      <div onKeyDownCapture={handleKeyDown} className={cn('relative', className)} role='region' aria-roledescription='carousel' aria-label='Image carousel' aria-live='polite' data-slot='carousel' {...props}>
        {children}
      </div>
    </CarouselContext.Provider>
  );
};

const CarouselContent = ({ className, ...props }: React.ComponentProps<'div'>) => {
  const { carouselRef, orientation } = useCarousel();

  return (
    <div ref={carouselRef} className='overflow-hidden' data-slot='carousel-content'>
      <div className={cn('flex', orientation === 'horizontal' ? '-ml-6' : '-mt-6 flex-col', className)} {...props} />
    </div>
  );
};

const CarouselItem = ({ className, ...props }: React.ComponentProps<'div'>) => {
  const { orientation } = useCarousel();

  return <div role='group' aria-roledescription='slide' data-slot='carousel-item' className={cn('min-w-0 shrink-0 grow-0 basis-full', orientation === 'horizontal' ? 'pl-6' : 'pt-6', className)} {...props} />;
};

const CarouselPrevious = ({ className, variant = 'outline', size = 'icon', ...props }: React.ComponentProps<typeof Button>) => {
  const { orientation, scrollPrev, canScrollPrev } = useCarousel();

  return (
    <Button
      data-slot='carousel-previous'
      variant={variant}
      size={size}
      className={cn('absolute h-10 w-10 rounded-full bg-card shadow-md flex items-center justify-center', orientation === 'horizontal' ? 'top-1/2 left-2 -translate-y-1/2' : '-top-12 left-1/2 -translate-x-1/2 rotate-90', className)}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      {...props}
    >
      <Icons.ArrowLeft className='w-4 h-4' />
      <span className='sr-only'>Previous slide</span>
    </Button>
  );
};

const CarouselNext = ({ className, variant = 'outline', size = 'icon', ...props }: React.ComponentProps<typeof Button>) => {
  const { orientation, scrollNext, canScrollNext } = useCarousel();

  return (
    <Button
      data-slot='carousel-next'
      variant={variant}
      size={size}
      className={cn('absolute h-10 w-10 rounded-full bg-card shadow-md flex items-center justify-center', orientation === 'horizontal' ? 'top-1/2 right-2 -translate-y-1/2' : '-bottom-12 left-1/2 -translate-x-1/2 rotate-90', className)}
      disabled={!canScrollNext}
      onClick={scrollNext}
      {...props}
    >
      <Icons.LayoutGrid className='w-4 h-4 rotate-90' />
      <span className='sr-only'>Next slide</span>
    </Button>
  );
};

export { type CarouselApi, Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext };

const CarouselDots = ({ className }: { className?: string }) => {
  const { api, scrollPrev, scrollNext, canScrollPrev, canScrollNext } = useCarousel();
  const [count, setCount] = React.useState(0);
  const [selected, setSelected] = React.useState(0);

  React.useEffect(() => {
    if (!api) return;
    const update = () => {
      setCount(api.scrollSnapList().length);
      setSelected(api.selectedScrollSnap());
    };
    update();
    api.on('reInit', update);
    api.on('select', update);
    return () => {
      api.off('reInit', update);
      api.off('select', update);
    };
  }, [api]);

  if (!count) return null;

  return (
    <div className={cn('flex items-center justify-center mt-4 space-x-4', className)}>
      {/* Prev button (inline, subtle) */}
      <button onClick={() => scrollPrev()} aria-label='Previous slide' disabled={!canScrollPrev} className={cn('p-2 rounded-full transition-colors text-muted-foreground disabled:opacity-40 hover:text-foreground', !canScrollPrev && 'cursor-default')}>
        <Icons.ArrowLeft className='w-4 h-4' />
      </button>

      {/* Dots */}
      <div className='flex items-center space-x-2'>
        {Array.from({ length: count }).map((_, i) => (
          <button key={i} onClick={() => api?.scrollTo(i)} aria-label={`Go to slide ${i + 1}`} aria-current={i === selected ? 'true' : 'false'} className={cn('w-2.5 h-2.5 rounded-full transition-colors', i === selected ? 'bg-primary' : 'bg-muted')} />
        ))}
      </div>

      {/* Next button (inline, subtle) */}
      <button onClick={() => scrollNext()} aria-label='Next slide' disabled={!canScrollNext} className={cn('p-2 rounded-full transition-colors text-muted-foreground disabled:opacity-40 hover:text-foreground', !canScrollNext && 'cursor-default')}>
        <Icons.LayoutGrid className='w-4 h-4 rotate-90' />
      </button>
    </div>
  );
};

export { CarouselDots };
